/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `assetUrl` for the Caintra Connect frontend.
 * @module utils/assetUrl
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import {
  TOKEN_EXPIRED_MESSAGE,
  TOKEN_EXPIRED_OR_INVALID_MESSAGE,
} from "./apiConstants.js";
import { Buffer } from "buffer";

const SENSITIVE_ASSET_PREFIXES = [
  "exports/",
  "chatFiles/",
  "companiesDocuments/",
  "appointmentsDocuments/",
  "proposals/",
];

const PUBLIC_LOGO_KEY_PATTERN = /^companiesDocuments\/[^/]+\/logo\//;
const PUBLIC_REF_PREFIX = "p.";
const RESTRICTED_REF_PREFIX = "r.";
const SIGNED_REF_PREFIX = "s.";
/** URL-safe base64 is padded to a multiple of this length before decoding. */
const BASE64_PAD = 4;
/** Object keys longer than this are rejected as paths. */
const MAX_ASSET_KEY_LENGTH = 1024;

const CONTROL_CHARS_PATTERN = /[\u0000-\u001f\u007f]/;

/**
 * @returns {string} API root with a trailing slash.
 */
export const getApiBaseUrl = () => {
  const root = process.env.REACT_APP_ROOT_API || "/";
  return root.endsWith("/") ? root : `${root}/`;
};

/**
 * True when the URL already carries an S3 signature and must be left as-is.
 * @param {string} [url]
 * @returns {boolean}
 */
export const isAlreadyPresignedUrl = (url = "") =>
  String(url).includes("X-Amz-Signature=") ||
  String(url).includes("X-Amz-Algorithm=");

/**
 * True for data, blob, and bundled static sources that never go through the proxy.
 * @param {string} [src]
 * @returns {boolean}
 */
export const isInlineOrLocalSrc = (src = "") => {
  const value = String(src);
  return (
    value.startsWith("data:") ||
    value.startsWith("blob:") ||
    value.startsWith("/static/")
  );
};

/**
 * True for the Caintra assets bucket, its CloudFront host, or the configured bucket host.
 * @param {string} [hostname]
 * @returns {boolean}
 */
export const isAssetsHost = (hostname = "") => {
  const host = String(hostname).toLowerCase();
  if (
    host.includes("caintra-connect-assets-") &&
    host.includes("amazonaws.com")
  ) {
    return true;
  }
  if (host.includes("cloudfront.net")) {
    return true;
  }
  try {
    const bucketHost = new URL(process.env.REACT_APP_BUCKET_URL || "").hostname;
    return Boolean(bucketHost) && hostname === bucketHost;
  } catch {
    return false;
  }
};

/** Absolute prefix for GET /assets/{ref}. */
const getAssetProxyPrefix = () => `${getApiBaseUrl()}assets/`;

/**
 * True when the value already points at the asset proxy, relative or absolute.
 * @param {string} [src]
 * @returns {boolean}
 */
export const isAssetProxyUrl = (src = "") => {
  const value = String(src).trim();
  if (!value) return false;
  if (value.startsWith("/assets/") || value.startsWith("assets/")) return true;
  const prefix = getAssetProxyPrefix();
  return value.startsWith(prefix);
};

/**
 * Returns the opaque ref token from an /assets path or a bare p., r., or s. token.
 * @param {string} [src]
 * @returns {string}
 */
export const extractAssetRefToken = (src = "") => {
  const raw = String(src || "").trim();
  if (!raw) return "";
  const fromPath = raw.match(/(?:^|\/)assets\/([^/?#]+)/);
  if (fromPath?.[1]) return fromPath[1];
  if (/^[prs]\./.test(raw) && !raw.includes("/") && !raw.includes("://")) {
    return raw.split("?")[0];
  }
  return "";
};

/**
 * Encodes a UTF-8 string as URL-safe base64 with padding removed.
 * @param {string} value
 * @returns {string}
 */
const toBase64Url = (value) =>
  Buffer.from(value, "utf8")
    .toString("base64")
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

/**
 * Decodes URL-safe base64 to a UTF-8 string.
 * Padding is restored before decode, and any failure returns an empty string.
 * @param {string} value
 * @returns {string}
 */
const fromBase64Url = (value) => {
  try {
    const padded = String(value).replace(/-/g, "+").replace(/_/g, "/");
    const pad =
      padded.length % BASE64_PAD === 0
        ? ""
        : "=".repeat(BASE64_PAD - (padded.length % BASE64_PAD));
    return Buffer.from(padded + pad, "base64").toString("utf8");
  } catch {
    return "";
  }
};

/**
 * Strips a hash, query, and leading slashes so comparisons use the object key only.
 * @param {string} [value]
 * @returns {string}
 */
export const normalizeAssetKey = (value = "") =>
  String(value)
    .split("#")[0]
    .split("?")[0]
    .replace(/^\/+/, "")
    .trim();

/**
 * Rejects empty, oversized, escaped, or traversal keys before they are encoded into a ref.
 * @param {string} key
 * @returns {boolean}
 */
export const isSafeAssetKey = (key) => {
  if (typeof key !== "string" || !key) {
    return false;
  }
  if (key.length > MAX_ASSET_KEY_LENGTH) {
    return false;
  }
  if (CONTROL_CHARS_PATTERN.test(key)) {
    return false;
  }
  if (key.includes("\\") || key.includes("\0")) {
    return false;
  }
  if (key.includes("://") || key.includes("..")) {
    return false;
  }
  if (key.startsWith("/") || key.startsWith("~")) {
    return false;
  }
  const segments = key.split("/");
  return segments.every(
    (segment) => segment && segment !== "." && segment !== "..",
  );
};

/**
 * True for document, chat, export, and proposal prefixes that require a download token.
 * @param {string} s3Key
 * @returns {boolean}
 */
export const isSensitiveAssetKey = (s3Key) => {
  const key = normalizeAssetKey(s3Key);
  return SENSITIVE_ASSET_PREFIXES.some((prefix) => key.startsWith(prefix));
};

/**
 * True when the key may be shown through the public proxy. Company logos are the exception inside document prefixes.
 * @param {string} s3Key
 * @returns {boolean}
 */
export const isPublicDisplayKey = (s3Key) => {
  const key = normalizeAssetKey(s3Key);
  if (!isSafeAssetKey(key)) return false;
  if (isSensitiveAssetKey(key)) {
    return PUBLIC_LOGO_KEY_PATTERN.test(key);
  }
  return true;
};

/**
 * Decodes a p. or r. ref back to an object key. Signed s. refs are not pointers.
 * @param {string} [ref]
 * @returns {string}
 */
const decodePointerAssetRef = (ref = "") => {
  const raw = String(ref).split("?")[0];
  if (
    !raw.startsWith(PUBLIC_REF_PREFIX) &&
    !raw.startsWith(RESTRICTED_REF_PREFIX)
  ) {
    return "";
  }
  const prefix = raw.startsWith(PUBLIC_REF_PREFIX)
    ? PUBLIC_REF_PREFIX
    : RESTRICTED_REF_PREFIX;
  const key = fromBase64Url(raw.slice(prefix.length));
  return isSafeAssetKey(key) ? key : "";
};

/**
 * Extracts an object key from a relative path, historical assets URL, or public proxy URL.
 * Signed proxy refs are intentionally opaque and return an empty key.
 * @param {string} src
 * @returns {string}
 */
export const extractAssetsObjectKey = (src = "") => {
  if (!src) return "";
  const raw = String(src).trim();
  if (!raw || isInlineOrLocalSrc(raw)) return "";

  if (isAssetProxyUrl(raw)) {
    const ref = extractAssetRefToken(raw);
    if (ref.startsWith(SIGNED_REF_PREFIX)) return "";
    return decodePointerAssetRef(ref);
  }

  if (raw.startsWith("http://") || raw.startsWith("https://")) {
    try {
      const parsed = new URL(raw.split("#")[0]);
      if (!isAssetsHost(parsed.hostname)) {
        return "";
      }
      return decodeURIComponent(parsed.pathname.replace(/^\/+/, ""));
    } catch {
      return "";
    }
  }

  return normalizeAssetKey(raw);
};

/**
 * Builds a public p. ref for a display key. Sensitive keys return an empty ref.
 * @param {string} s3Key
 * @returns {string}
 */
export const createPublicAssetRef = (s3Key) => {
  const key = extractAssetsObjectKey(s3Key) || normalizeAssetKey(s3Key);
  if (!isSafeAssetKey(key) || !isPublicDisplayKey(key)) {
    return "";
  }
  return `${PUBLIC_REF_PREFIX}${toBase64Url(key)}`;
};

/**
 * Builds an r. ref that can be exchanged for a presigned download without sending the raw key.
 * @param {string} s3Key
 * @returns {string}
 */
export const createRestrictedAssetRef = (s3Key) => {
  const key = extractAssetsObjectKey(s3Key) || normalizeAssetKey(s3Key);
  if (!isSafeAssetKey(key)) {
    return "";
  }
  return `${RESTRICTED_REF_PREFIX}${toBase64Url(key)}`;
};

const SKIP_RESTORE_FIELDS = new Set([
  "uploadUrl",
  "presignedUrl",
  "presignedUrls",
  "password",
  "token",
  "refreshToken",
  "s3Path",
  "assetRef",
]);

/**
 * Walks API payloads and turns proxy URLs back into object keys, leaving credentials and upload URLs untouched.
 * @param {*} value
 * @returns {*}
 */
export const restoreIncomingAssets = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => restoreIncomingAssets(item));
  }
  if (value && typeof value === "object") {
    const next = {};
    for (const [key, child] of Object.entries(value)) {
      if (SKIP_RESTORE_FIELDS.has(key)) {
        next[key] = child;
      } else {
        next[key] = restoreIncomingAssets(child);
      }
    }
    return next;
  }
  if (typeof value === "string") {
    const ref = extractAssetRefToken(value);
    if (!ref) return value;
    const key = decodePointerAssetRef(ref);
    return key || value;
  }
  return value;
};

/**
 * Prefixes a relative asset path with the API root. Absolute URLs are returned unchanged.
 * @param {string} [pathOrUrl]
 * @returns {string}
 */
const toAbsoluteAssetUrl = (pathOrUrl = "") => {
  const value = String(pathOrUrl).trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  return `${getApiBaseUrl()}${value.replace(/^\/+/, "")}`;
};

/**
 * Resolves a media or document reference to GET /assets/{ref}.
 * The browser never receives a bucket hostname or a raw object key.
 * @param {string} src
 * @returns {string}
 */
export const generateAssetUrl = (src = "") => {
  if (!src) return "";
  if (isInlineOrLocalSrc(src) || isAlreadyPresignedUrl(src)) return src;
  if (isAssetProxyUrl(src)) {
    const pathOnly = String(src).split("?")[0].split("#")[0];
    return toAbsoluteAssetUrl(pathOnly);
  }

  const pathOnly = String(src).split("?")[0].split("#")[0].trim();
  if (/^[prs]\./.test(pathOnly) && !pathOnly.includes("/")) {
    return `${getAssetProxyPrefix()}${pathOnly}`;
  }

  // Absolute S3/CloudFront URLs stay as-is until GET /assets is deployed.
  // Rewriting them to the proxy is what blanked the homepage banners.
  if (src.startsWith("http://") || src.startsWith("https://")) {
    return src;
  }

  const key = extractAssetsObjectKey(src);
  const ref = createPublicAssetRef(key);
  if (!ref) return "";
  return `${getAssetProxyPrefix()}${ref}`;
};

const BINARY_FETCH_ACCEPT =
  "image/png,image/jpeg,image/jpg,image/webp,image/gif,application/octet-stream";

/**
 * Loads a proxy asset with an Accept header API Gateway will treat as binary.
 * Native <img src> sends image/avif first and receives base64 text (HTTP 200).
 * @param {string} src
 * @returns {Promise<string>} object URL or a passthrough src
 */
export const fetchAssetObjectUrl = async (src = "") => {
  const url = generateAssetUrl(src);
  if (!url) return "";
  if (isInlineOrLocalSrc(url) || isAlreadyPresignedUrl(url)) return url;
  if (!isAssetProxyUrl(url)) return url;

  const response = await fetch(url, {
    headers: { Accept: BINARY_FETCH_ACCEPT },
    mode: "cors",
    credentials: "omit",
  });
  if (!response.ok) return "";
  const blob = await response.blob();
  if (!blob || blob.size === 0) return "";
  const type = blob.type || "";
  if (
    type.startsWith("text/") ||
    type.includes("json") ||
    type.includes("html")
  ) {
    return "";
  }
  return URL.createObjectURL(blob);
};

/**
 * Same resolution as generateAssetUrl, plus a cache-busting query for proxied images.
 * @param {string} [src]
 * @returns {string}
 */
export const generateImageUrl = (src = "") => {
  if (!src) return "";
  if (isInlineOrLocalSrc(src) || isAlreadyPresignedUrl(src)) return src;

  const resolved = generateAssetUrl(src);
  if (!resolved) return "";
  if (isInlineOrLocalSrc(resolved) || isAlreadyPresignedUrl(resolved)) {
    return resolved;
  }

  const separator = resolved.includes("?") ? "&" : "?";
  return `${resolved}${separator}v=${Date.now()}`;
};

/**
 * Reuses an existing r. token or creates one from the object key.
 * @param {string} src
 * @returns {string}
 */
const toRestrictedAssetRef = (src) => {
  const existing = extractAssetRefToken(src);
  if (existing && existing.startsWith(RESTRICTED_REF_PREFIX)) {
    return existing;
  }
  const key = extractAssetsObjectKey(src);
  return key ? createRestrictedAssetRef(key) : "";
};

/**
 * Exchanges an r. ref for a short-lived download URL and retries once after a token refresh.
 * @param {string} src
 * @param {Function} getToken
 * @param {Function} refreshToken
 * @returns {Promise<string|null>}
 */
const fetchPresignedDownloadUrl = async (src, getToken, refreshToken) => {
  const token = getToken();
  const assetRef = toRestrictedAssetRef(src);
  if (!token || !assetRef) return null;

  const response = await fetch(`${getApiBaseUrl()}upload/presigned-download`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ assetRef }),
  });

  if (response.ok) {
    const data = await response.json();
    return toAbsoluteAssetUrl(data.downloadUrl || "");
  }

  const errorData = await response.json().catch(() => ({}));
  if (
    errorData?.message === TOKEN_EXPIRED_MESSAGE ||
    errorData?.message === TOKEN_EXPIRED_OR_INVALID_MESSAGE
  ) {
    const newTokens = await refreshToken();
    if (!newTokens) return null;
    return fetchPresignedDownloadUrl(src, getToken, refreshToken);
  }
  return null;
};

/**
 * Resolves a media or document reference to a URL the browser can GET.
 * Sensitive prefixes are exchanged for a short-lived presigned S3 URL.
 * @param {string} src
 * @param {{ getToken: Function, refreshToken: Function }} deps
 * @returns {Promise<string>}
 */
export const resolveAccessibleAssetUrl = async (
  src = "",
  { getToken, refreshToken } = {},
) => {
  if (!src) return "";
  if (isInlineOrLocalSrc(src) || isAlreadyPresignedUrl(src)) return src;

  const ref = extractAssetRefToken(src);
  if (ref.startsWith(RESTRICTED_REF_PREFIX)) {
    if (!getToken || !refreshToken) return "";
    return (await fetchPresignedDownloadUrl(src, getToken, refreshToken)) || "";
  }
  if (isAssetProxyUrl(src) || ref.startsWith(PUBLIC_REF_PREFIX) || ref.startsWith(SIGNED_REF_PREFIX)) {
    return toAbsoluteAssetUrl(src);
  }

  const key = extractAssetsObjectKey(src);
  if (key && isSensitiveAssetKey(key) && !isPublicDisplayKey(key)) {
    if (!getToken || !refreshToken) return "";
    return (await fetchPresignedDownloadUrl(src, getToken, refreshToken)) || "";
  }
  if (key) {
    return generateAssetUrl(key);
  }
  return src;
};
